import { Box, Text } from '@vouched.id/vault';
import React, { useMemo } from 'react';
import { Tooltips } from '../../tooltips';
import { itemHasErrorOrWarning } from '../../utils';
import { Dropdown } from '../Dropdown';
import { DropdownItemsSection } from '../DropdownItemsSection';
import { addIconsToDropdownItems, DropdownItemTypes, DropdownStatusIcon } from '../utils';
import { buildDeviceData, buildExtractedData, buildSubmittedData, IDVDetailsTable } from './IDVDetailsTable';
import { IdBarcodeMismatchDetails } from 'components/account/jobs-v2/dropdowns/IdDocument/IdBarcodeMismatchDetails';
import { IdNameMismatchDetails } from 'components/account/jobs-v2/dropdowns/IdDocument/IdNameMismatchDetails';
import { IdvDetails } from 'components/account/jobs-v2/dropdowns/IdDocument/IdvDetails';
import { getRejReasonsNamesAndDescriptions, ID_PENALTY_KEYS, IdDocumentItemTitle, joinNonEmpty, SELFIE_PENALTY_KEYS } from 'components/account/jobs-v2/dropdowns/IdDocument/utils';
import { ID_BARCODE_MISMATCH_KEYS, ID_NAME_MISMATCH_KEYS, IdvError } from 'models/jobs/jobs';
export const IdDocument = ({ jobRequestInfo, jobRequestParams, jobRequestProperties, jobResult, jobErrors, jobStatus, rejectionReasons }) => {
    const { barcodeData, clientOutput, confidences, country, hasPDF417Back, motorcycle, ocrBarcodeMismatches, ocrData, state, type: idType, unverifiedIdAddress, firstName, middleName, lastName } = jobResult;
    const isDigitalIdCapture = clientOutput?.capture?.id === 'digitalID';
    const idConfidence = confidences?.id;
    const idExpiredConfidence = confidences?.idExpired;
    const selfieConfidence = confidences?.selfie;
    const faceMatchConfidence = confidences?.faceMatch;
    const barcodeMatchConfidence = confidences?.barcodeMatch;
    const nameMatchConfidence = confidences?.nameMatch;
    const birthDateMatchConfidence = confidences?.birthDateMatch;
    const livenessPerformedConfidence = confidences?.liveness;
    const stableIdPatternConfidence = confidences?.stableIdPattern;
    const stableSelfiePatternConfidence = confidences?.stableSelfiePattern;
    const voidedIdConfidence = confidences?.voidedId;
    const submittedData = buildSubmittedData(jobRequestParams);
    const nameMatchErrorWarning = itemHasErrorOrWarning(jobErrors, IdvError.NameMatchError);
    const { dropdownHasWarnings, dropdownHasRejections, dropdownItems } = useMemo(() => {
        const dropdownItemsWithoutIcons = [
            isDigitalIdCapture
                ? {
                    text: IdDocumentItemTitle.ValidDigitalId,
                    type: DropdownItemTypes.ConfidenceDropdownItem,
                    available: true,
                    hasErrorOrWarning: { hasError: false, hasWarning: false },
                    tooltip: Tooltips.ValidDigitalId
                }
                : {
                    text: IdDocumentItemTitle.ValidPhysicalId,
                    type: DropdownItemTypes.ConfidenceDropdownItem,
                    available: typeof idConfidence === 'number',
                    hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.InvalidIdPhotoError),
                    tooltip: Tooltips.ValidPhysicalId,
                    penalties: getRejReasonsNamesAndDescriptions(rejectionReasons, ID_PENALTY_KEYS)
                },
            {
                text: IdDocumentItemTitle.ValidSelfie,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof selfieConfidence === 'number',
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.InvalidUserPhotoError),
                tooltip: Tooltips.ValidSelfie,
                penalties: getRejReasonsNamesAndDescriptions(rejectionReasons, SELFIE_PENALTY_KEYS)
            },
            {
                text: IdDocumentItemTitle.SelfieLivenessPerformed,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof livenessPerformedConfidence === 'number',
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.LivenessSkippedError),
                tooltip: Tooltips.SelfieLivenessPerformed
            },
            {
                text: IdDocumentItemTitle.ExpirationDate,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof idExpiredConfidence === 'number',
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.ExpiredIdError),
                tooltip: Tooltips.ExpirationDate
            },
            {
                text: IdDocumentItemTitle.IdMatchesSelfie,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof faceMatchConfidence === 'number',
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.FaceMatchError),
                tooltip: Tooltips.IdMatchesSelfie
            },
            {
                text: IdDocumentItemTitle.IdMatchesBarcode,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof barcodeMatchConfidence === 'number',
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.BarcodeMatchError),
                tooltip: Tooltips.IdMatchesBarcode,
                penalties: getRejReasonsNamesAndDescriptions(rejectionReasons, ID_BARCODE_MISMATCH_KEYS),
                customComponent: (React.createElement(IdBarcodeMismatchDetails, { barcodeData: barcodeData, customStyles: {
                        gridColumn: 'span 3'
                    }, ocrBarcodeMismatches: ocrBarcodeMismatches, ocrData: ocrData }))
            },
            {
                text: IdDocumentItemTitle.IdMatchesSubmittedName,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof nameMatchConfidence === 'number',
                hasErrorOrWarning: nameMatchErrorWarning,
                tooltip: Tooltips.IdMatchesSubmittedName,
                penalties: getRejReasonsNamesAndDescriptions(rejectionReasons, ID_NAME_MISMATCH_KEYS),
                customComponent: (React.createElement(IdNameMismatchDetails, { customStyles: {
                        gridColumn: 'span 3'
                    }, hasNameMatchError: nameMatchErrorWarning.hasError, identityName: joinNonEmpty([firstName, middleName, lastName]), submittedName: joinNonEmpty([
                        submittedData.firstName,
                        submittedData.lastName
                    ]) }))
            },
            {
                text: IdDocumentItemTitle.IdMatchesSubmittedDob,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof birthDateMatchConfidence === 'number',
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.BirthDateMatchError),
                tooltip: Tooltips.IdMatchesSubmittedDOB
            },
            {
                text: IdDocumentItemTitle.BarcodePresence,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: !!clientOutput?.includeBarcode && !!hasPDF417Back,
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.BarcodeDetectError),
                tooltip: Tooltips.BarcodePresence
            },
            {
                text: IdDocumentItemTitle.IdRestrictions,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: true,
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.RestrictedIdDocumentError),
                tooltip: Tooltips.IdMatchesConstraints
            },
            {
                text: IdDocumentItemTitle.StableIdPattern,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof stableIdPatternConfidence === 'number',
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.StableIdPatternError),
                tooltip: Tooltips.StableIdPattern
            },
            {
                text: IdDocumentItemTitle.StableSelfiePattern,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof stableSelfiePatternConfidence === 'number',
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.StableSelfiePatternError),
                tooltip: Tooltips.StableSelfiePattern
            },
            {
                text: IdDocumentItemTitle.DocumentVoided,
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof voidedIdConfidence === 'number',
                hasErrorOrWarning: itemHasErrorOrWarning(jobErrors, IdvError.DocumentVoidedError),
                tooltip: Tooltips.DocumentVoided
            }
        ];
        return addIconsToDropdownItems(dropdownItemsWithoutIcons);
    }, [
        barcodeData,
        clientOutput,
        hasPDF417Back,
        ocrBarcodeMismatches,
        ocrData,
        jobErrors,
        itemHasErrorOrWarning,
        isDigitalIdCapture,
        idConfidence,
        idExpiredConfidence,
        selfieConfidence,
        faceMatchConfidence,
        barcodeMatchConfidence,
        nameMatchConfidence,
        birthDateMatchConfidence,
        livenessPerformedConfidence,
        stableIdPatternConfidence,
        stableSelfiePatternConfidence,
        voidedIdConfidence,
        rejectionReasons,
        nameMatchErrorWarning,
        submittedData,
        firstName,
        middleName,
        lastName
    ]);
    return (React.createElement(Dropdown, { statusIcon: React.createElement(DropdownStatusIcon, { dropdownHasRejections: dropdownHasRejections, dropdownHasWarnings: dropdownHasWarnings, parentJobStatus: jobStatus }), title: "Visual IDV" },
        React.createElement(Box, null,
            React.createElement(Text, null, "This section contains data checks performed against the submitted ID Document and Selfie photos."),
            React.createElement(DropdownItemsSection, { dropdownItems: dropdownItems }),
            React.createElement(IDVDetailsTable, { deviceData: buildDeviceData(jobResult, jobRequestInfo), extractedData: buildExtractedData(jobResult), header: "Final Identity", properties: jobRequestProperties, submittedData: submittedData }),
            ocrData && (React.createElement(IdvDetails, { barcodeData: barcodeData, captureType: clientOutput?.capture?.id, country: country, idType: idType, motorcycle: motorcycle, ocrData: ocrData, state: state, unverifiedIdAddress: unverifiedIdAddress })))));
};
